import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Badge,
  Icon,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Alert,
  AlertIcon,
} from '@chakra-ui/react'
import { LuBeaker, LuSearch, LuArrowRight } from 'react-icons/lu'
import { MdArrowBack } from 'react-icons/md'
import { pathologyDisplayName } from './utils/displayName'
import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

export interface ImmunoBlot {
  id: string
  // `name` must match the pathology value stored in DB / sent to backend API.
  // Do not edit these — they are the canonical pathology identifiers.
  name: string
  // `displayName` is what we show in the UI. Falls back to `name` when absent.
  displayName?: string
  antibodyType: 'IgG' | 'IgM' | 'IgG/IgM'
  category: string
  colorScheme: string
  comingSoon?: boolean
  // Small ribbon on the card, e.g. "Nouveau · Beta" — for a newly-launched
  // entry we want to draw attention to without a whole separate section.
  badgeLabel?: string
  /**
   * À quel module du Lab Studio cette entrée appartient. Absent = IRIS,
   * puisque tout le catalogue historique est à lui.
   *
   * PROTIS partage l'atelier d'IRIS — même dépôt, même galerie, même export —
   * mais pas son catalogue : un biologiste qui ouvre IRIS cherche un test de
   * fabricant, pas un gel de recherche. Les deux listes sont donc la même
   * table, lue à travers ce champ.
   */
  module?: 'iris' | 'protis'
}

// Catalogue d'immunoblots (Western Blot) supportés par Careflai.
// `name` reste aligné sur les valeurs « pathology » en base ; `displayName`
// retire les préfixes fournisseurs pour l'affichage utilisateur.
export const IMMUNOBLOTS: ImmunoBlot[] = [
  {
    // PROTIS — Western blot de recherche, monté au laboratoire.
    //
    // Seule entrée du catalogue qui ne soit pas un test de fabricant : pas de
    // ligne de contrôle imprimée, pas de bandes nommées, une échelle donnée
    // par le marqueur de masse déposé sur le gel. Son mode d'interprétation
    // est `ladder_profile` (voir la configuration PROTIS-WB en base), à côté
    // de `paired_comparison` et `zone_count` qui s'écartent déjà du verdict
    // sur bandes nommées. `antibodyType` ne veut rien dire ici — on ne révèle
    // pas une classe d'anticorps mais un profil protéique — mais le champ est
    // obligatoire dans le catalogue, d'où la valeur la plus neutre.
    id: 'protis-wb',
    module: 'protis',
    name: 'PROTIS WESTERN BLOT',
    displayName: 'PROTIS — Profils protéiques',
    antibodyType: 'IgG/IgM',
    category: 'Recherche',
    colorScheme: 'cyan',
    badgeLabel: 'Nouveau · Beta',
  },
  {
    // Dynablot (CHU Bordeaux) reports can mix several self-tests in one PDF
    // scan — a normal upload forces picking ONE pathology up front, which
    // doesn't fit that shape. This entry skips that requirement: the AI
    // service reads each strip's own printed pathology name and groups the
    // results by it (see pathology_label) — no single pathology to pick.
    id: 'dynablot-multitest',
    name: 'DYNABLOT MULTITEST',
    displayName: 'Multitest Dynablot',
    antibodyType: 'IgG/IgM',
    category: 'Multitest',
    colorScheme: 'teal',
    badgeLabel: 'Nouveau · Beta',
  },
  {
    id: 'ldbio-aspergillus-igg',
    name: 'LDBIO ASPERGILLUS WB IgG',
    displayName: 'ASPERGILLUS WB IgG',
    antibodyType: 'IgG',
    category: 'Fungal',
    colorScheme: 'green',
  },
  {
    id: 'chagas-igg',
    name: 'CHAGAS WB IgG',
    antibodyType: 'IgG',
    category: 'Parasitic',
    colorScheme: 'orange',
    comingSoon: true,
  },
  {
    id: 'cysticercosis-igg',
    name: 'LDBIO CYSTICERCOSIS WB IgG',
    displayName: 'CYSTICERCOSIS WB IgG',
    antibodyType: 'IgG',
    category: 'Parasitic',
    colorScheme: 'orange',
  },
  {
    id: 'ldbio-echinococcus-igg',
    name: 'LDBIO ECHINOCOCCUS WB IgG',
    displayName: 'ECHINOCOCCUS WB IgG',
    antibodyType: 'IgG',
    category: 'Parasitic',
    colorScheme: 'orange',
  },
  {
    id: 'fasciola-es-igg',
    name: 'FASCIOLA ES WB IgG',
    antibodyType: 'IgG',
    category: 'Parasitic',
    colorScheme: 'orange',
    comingSoon: true,
  },
  {
    id: 'ldbio-toxo-ii-igg',
    name: 'LDBIO TOXO II IgG CONFIRMATION',
    displayName: 'TOXOPLASMA II IgG CONFIRMATION',
    antibodyType: 'IgG',
    category: 'Parasitic',
    colorScheme: 'purple',
  },
  {
    id: 'ldbio-toxo-ii-igm',
    name: 'LDBIO TOXO II IgM CONFIRMATION',
    displayName: 'TOXOPLASMA II IgM CONFIRMATION',
    antibodyType: 'IgM',
    category: 'Parasitic',
    colorScheme: 'pink',
  },
  {
    id: 'leishmania-igg',
    name: 'LDBIO LEISHMANIA WB IgG',
    displayName: 'LEISHMANIA WB IgG',
    antibodyType: 'IgG',
    category: 'Parasitic',
    colorScheme: 'orange',
  },
  {
    id: 'peo-igg',
    name: 'PEO WB IgG',
    antibodyType: 'IgG',
    category: 'Autoimmune',
    colorScheme: 'blue',
    comingSoon: true,
  },
  {
    id: 'ldbio-schisto-ii-igg',
    name: 'LDBIO SCHISTO II WB IgG',
    displayName: 'SCHISTOSOMA II WB IgG',
    antibodyType: 'IgG',
    category: 'Parasitic',
    colorScheme: 'orange',
  },
  {
    id: 'ldbio-toxocara-wb-igg',
    name: 'LDBIO TOXOCARA WB IgG CONFIRMATION',
    displayName: 'TOXOCARA WB IgG CONFIRMATION',
    antibodyType: 'IgG',
    category: 'Parasitic',
    colorScheme: 'orange',
  },
  {
    // CIP-WB (Comparaison des Profils Immunologiques) — a DIFFERENT kit from
    // LDBIO TOXO II above: it never uses a manufacturer témoin, it compares
    // two SAMPLE strips to each other (mère/enfant, J0/J+N, ou sérum/humeur
    // aqueuse). The Dynablot report prints this kit's strips as plain
    // "Toxoplasma IgG"/"Toxoplasma IgM" (no "II") — see resolvePathologyLabel.
    id: 'toxoplasma-cip-igg',
    name: 'LDBIO TOXOPLASMA CIP-WB IgG',
    displayName: 'TOXOPLASMA CIP-WB IgG (comparaison mère/enfant)',
    antibodyType: 'IgG',
    category: 'Parasitic',
    colorScheme: 'purple',
  },
  {
    id: 'toxoplasma-cip-igm',
    name: 'LDBIO TOXOPLASMA CIP-WB IgM',
    displayName: 'TOXOPLASMA CIP-WB IgM (comparaison mère/enfant)',
    antibodyType: 'IgM',
    category: 'Parasitic',
    colorScheme: 'pink',
  },
  {
    id: 'trichinella-es-igg',
    // Le nom doit correspondre au champ `pathology` de la configuration : la
    // recherche se fait par égalité stricte côté serveur. `displayName` porte
    // la version courte montrée dans le catalogue.
    name: 'LDBIO TRICHINELLA ES WB IgG',
    displayName: 'TRICHINELLA ES WB IgG',
    antibodyType: 'IgG',
    category: 'Parasitic',
    colorScheme: 'orange',
  },
]

// Bordeaux/Dynablot multitest: matches a strip's OCR-read printed self-test
// name (e.g. "Aspergillus IgG", "Toxo II IgM", "Toxocara IgG LCR/HA") to a
// real catalog entry, so multitest cases get the SAME per-pathology band
// interpretation as a normal single-pathology upload instead of none.
//
// Keyword-based, not exact-string: the printed name is Dynablot's own report
// wording, never identical to our catalog `name` values, and OCR adds its own
// noise on top (verified: "Aspergilus" missing an L on a real report). Each
// keyword is checked before any shorter keyword it's a substring of (e.g.
// "TOXOCARA" before "TOXO") so the more specific one wins.
const _PATHOLOGY_KEYWORD_MAP: Array<{ keywords: string[]; igm?: boolean; id: string }> = [
  { keywords: ['ASPERGILLUS'], id: 'ldbio-aspergillus-igg' },
  { keywords: ['SCHISTO'], id: 'ldbio-schisto-ii-igg' },
  { keywords: ['ECHINOCOCCUS'], id: 'ldbio-echinococcus-igg' },
  { keywords: ['CHAGAS'], id: 'chagas-igg' },
  { keywords: ['CYSTICERCOSIS'], id: 'cysticercosis-igg' },
  { keywords: ['FASCIOLA'], id: 'fasciola-es-igg' },
  { keywords: ['TRICHINELLA'], id: 'trichinella-es-igg' },
  { keywords: ['PEO'], id: 'peo-igg' },
  { keywords: ['LEISHMANIA'], id: 'leishmania-igg' },
  // Longer/more specific keywords first: "TOXOCARA" before "TOXO", and only
  // the plain confirmation test (no LCR/HA — a different sample matrix we
  // don't have a config for at all, deliberately left unmatched below).
  { keywords: ['TOXOCARA'], id: 'ldbio-toxocara-wb-igg' },
  // CIP-WB comparé mère/enfant kit vs the LDBIO TOXO II CONFIRMATION kit are
  // two different products that print DIFFERENT self-test names on a real
  // Dynablot report (verified on case b1c28a42: the confirmation kit's
  // strips read "Toxo II IgG"/"Toxo II IgM", the CIP kit's strips read plain
  // "Toxoplasma IgG"/"Toxoplasma IgM" — no "II"). Checked first, on the
  // longer/more specific "TOXOPLASMA" keyword, so it never falls through to
  // the "TOXO" branch below meant for the confirmation kit only.
  { keywords: ['TOXOPLASMA'], igm: true, id: 'toxoplasma-cip-igm' },
  { keywords: ['TOXOPLASMA'], id: 'toxoplasma-cip-igg' },
  { keywords: ['TOXO'], igm: true, id: 'ldbio-toxo-ii-igm' },
  { keywords: ['TOXO'], id: 'ldbio-toxo-ii-igg' },
]

/**
 * Le module de catalogue auquel appartient un `pathologyId` enregistré sur un
 * cas. Inconnu = IRIS : tout l'historique lui appartient, et les surfaces qui
 * attribuent des cas à des applications (accueil, fil d'activité, tableau de
 * suivi) ne doivent jamais perdre un cas pour une entrée disparue du
 * catalogue.
 */
export const moduleOfPathologyId = (pathologyId: string | null | undefined): 'iris' | 'protis' =>
  IMMUNOBLOTS.find((b) => b.id === pathologyId)?.module ?? 'iris'

export function resolvePathologyLabel(rawLabel: string | null | undefined): ImmunoBlot | null {
  if (!rawLabel) return null
  const upper = rawLabel.toUpperCase()
  // Toxocara's LCR/HA variant is a different sample matrix with no config in
  // our catalog — never match it to the plain Toxocara confirmation test.
  if (upper.includes('TOXOCARA') && (upper.includes('LCR') || upper.includes('HA'))) return null
  const isIgm = /\bIGM\b/.test(upper)
  for (const entry of _PATHOLOGY_KEYWORD_MAP) {
    if (!!entry.igm !== isIgm) continue
    if (entry.keywords.some((kw) => upper.includes(kw))) {
      return IMMUNOBLOTS.find((b) => b.id === entry.id) ?? null
    }
  }
  return null
}

interface ImmunoBlotSelectionProps {
  onSelect: (immunoblot: ImmunoBlot) => void
  onBack?: () => void
  /** Le module qui ouvre le catalogue. IRIS par défaut. */
  module?: 'iris' | 'protis'
}

export default function ImmunoBlotSelection({ onSelect, onBack, module = 'iris' }: ImmunoBlotSelectionProps) {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')

  // Filtrer + trier : les tests disponibles d'abord, « bientôt » à la fin
  // (tri stable → l'ordre du catalogue est préservé dans chaque groupe).
  const filteredImmunoblots = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    const mine = IMMUNOBLOTS.filter((blot) => (blot.module ?? 'iris') === module)
    const base = query
      ? mine.filter(
          (blot) =>
            blot.name.toLowerCase().includes(query) ||
            blot.antibodyType.toLowerCase().includes(query) ||
            blot.category.toLowerCase().includes(query)
        )
      : mine
    return [...base].sort((a, b) => Number(!!a.comingSoon) - Number(!!b.comingSoon))
  }, [searchQuery, module])

  return (
    <Box>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <VStack spacing={2} align="start">
          {onBack && (
            <Button
              leftIcon={<Icon as={MdArrowBack} />}
              variant="ghost"
              size="sm"
              onClick={onBack}
              mb={2}
            >
              {t('blotAnalysis.immunoblotSelection.back')}
            </Button>
          )}
          <Heading as="h2" size="lg" color="teal.700">
            {t('blotAnalysis.immunoblotSelection.selectImmunoblot')}
          </Heading>
          <Text fontSize="md" color="gray.600">
            {t('blotAnalysis.immunoblotSelection.chooseImmunoblotType')}
          </Text>
        </VStack>

        {/* Search Bar */}
        <InputGroup size="lg" data-tour-id="blot-search">
          <InputLeftElement>
            <Icon as={LuSearch} color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder={t('blotAnalysis.immunoblotSelection.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            bg="white"
            borderColor="gray.300"
            _hover={{ borderColor: 'teal.400' }}
            _focus={{
              borderColor: 'teal.500',
              boxShadow: '0 0 0 1px var(--chakra-colors-teal-500)',
            }}
          />
        </InputGroup>

        {/* Results count */}
        <HStack justify="space-between" px={1}>
          <Text fontSize="sm" color="gray.600">
            {t('blotAnalysis.immunoblotSelection.immunoblotsAvailable', {
              count: filteredImmunoblots.filter(b => !b.comingSoon).length
            })}
          </Text>
          {searchQuery && (
            <Button size="xs" variant="ghost" onClick={() => setSearchQuery('')}>
              {t('blotAnalysis.immunoblotSelection.resetFilters')}
            </Button>
          )}
        </HStack>

        {/* ImmunoBlot Cards Grid — compact, available first */}
        {filteredImmunoblots.length > 0 ? (
          <SimpleGrid columns={{ base: 2, md: 3, xl: 4 }} spacing={3} data-tour-id="blot-catalog">
            {filteredImmunoblots.map((immunoblot) => {
              const cs = immunoblot.colorScheme
              const abScheme =
                immunoblot.antibodyType === 'IgG'
                  ? 'green'
                  : immunoblot.antibodyType === 'IgM'
                    ? 'pink'
                    : 'purple'
              const soon = !!immunoblot.comingSoon
              return (
                <Box
                  key={immunoblot.id}
                  role="group"
                  cursor={soon ? 'default' : 'pointer'}
                  onClick={() => !soon && onSelect(immunoblot)}
                  bg="white"
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="xl"
                  overflow="hidden"
                  position="relative"
                  transition="all 0.2s ease"
                  opacity={soon ? 0.62 : 1}
                  filter={soon ? 'grayscale(0.5)' : undefined}
                  _hover={
                    soon
                      ? {}
                      : {
                          transform: 'translateY(-3px)',
                          borderColor: `${cs}.300`,
                          boxShadow: `0 12px 22px -12px var(--chakra-colors-${cs}-400)`,
                        }
                  }
                >
                  {/* colored top accent */}
                  <Box h="3px" bgGradient={`linear(to-r, ${cs}.400, ${cs}.200)`} />
                  {immunoblot.badgeLabel && (
                    <Badge
                      position="absolute"
                      top={2}
                      right={2}
                      colorScheme={cs}
                      variant="solid"
                      borderRadius="full"
                      px={2}
                      fontSize="2xs"
                      fontWeight="700"
                      zIndex={1}
                    >
                      {immunoblot.badgeLabel}
                    </Badge>
                  )}
                  <Box p={3.5}>
                    <HStack justify="space-between" align="start" mb={2.5}>
                      <Box
                        w="36px"
                        h="36px"
                        borderRadius="lg"
                        bg={soon ? 'gray.100' : `${cs}.50`}
                        display="grid"
                        sx={{ placeItems: 'center' }}
                      >
                        <Icon as={LuBeaker} boxSize={4.5} color={soon ? 'gray.400' : `${cs}.500`} />
                      </Box>
                      {!immunoblot.badgeLabel && (
                        <Badge
                          colorScheme={soon ? 'gray' : abScheme}
                          variant={soon ? 'outline' : 'subtle'}
                          borderRadius="full"
                          px={2}
                          fontSize="2xs"
                          fontWeight="700"
                        >
                          {immunoblot.antibodyType}
                        </Badge>
                      )}
                    </HStack>

                    <Text
                      fontSize="sm"
                      fontWeight="700"
                      color={soon ? 'gray.500' : 'gray.800'}
                      lineHeight="1.25"
                      noOfLines={2}
                      minH="2.5em"
                    >
                      {pathologyDisplayName(immunoblot.displayName ?? immunoblot.name)}
                    </Text>

                    <HStack justify="space-between" mt={2.5}>
                      <HStack spacing={1.5} minW={0}>
                        <Box w="6px" h="6px" borderRadius="full" bg={soon ? 'gray.300' : `${cs}.400`} flexShrink={0} />
                        <Text fontSize="2xs" color="gray.500" fontWeight="500" noOfLines={1}>
                          {immunoblot.category}
                        </Text>
                      </HStack>
                      {soon ? (
                        <Text fontSize="2xs" color="gray.400" fontWeight="700" textTransform="uppercase" letterSpacing="wide">
                          Bientôt
                        </Text>
                      ) : (
                        <HStack
                          spacing={0.5}
                          color={`${cs}.600`}
                          opacity={{ base: 1, md: 0 }}
                          transform={{ base: 'none', md: 'translateX(-4px)' }}
                          transition="all 0.2s ease"
                          _groupHover={{ opacity: 1, transform: 'translateX(0)' }}
                          flexShrink={0}
                        >
                          <Text fontSize="2xs" fontWeight="700">
                            {t('blotAnalysis.immunoblotSelection.select')}
                          </Text>
                          <Icon as={LuArrowRight} boxSize={3} />
                        </HStack>
                      )}
                    </HStack>
                  </Box>
                </Box>
              )
            })}
          </SimpleGrid>
        ) : (
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <VStack align="start" spacing={1}>
              <Text fontWeight="semibold">{t('blotAnalysis.immunoblotSelection.noImmunoblotFound')}</Text>
              <Text fontSize="sm">
                {t('blotAnalysis.immunoblotSelection.tryModifyingSearch')}
              </Text>
            </VStack>
          </Alert>
        )}

        {/* Info Box */}
        <Box bg="teal.50" border="1px" borderColor="teal.200" borderRadius="md" p={4}>
          <HStack spacing={2} align="start">
            <Text fontSize="sm" color="teal.800">
              💡 <strong>{t('blotAnalysis.immunoblotSelection.information')} :</strong> {t('blotAnalysis.immunoblotSelection.immunoblotAnalysisInfo')}
            </Text>
          </HStack>
        </Box>

        {/* Disclaimer */}
        <Alert status="warning" borderRadius="md" variant="left-accent">
          <AlertIcon />
          <VStack align="start" spacing={1}>
            <Text fontWeight="semibold" fontSize="sm">
              {t('blotAnalysis.immunoblotSelection.diagnosticAssistanceTool')}
            </Text>
            <Text fontSize="xs">
              {t('blotAnalysis.immunoblotSelection.resultsDisclaimer')}
            </Text>
          </VStack>
        </Alert>
      </VStack>
    </Box>
  )
}
